/**
 * Approval Queue for Tenant Onboarding (DRR-073)
 *
 * Shows generated configs awaiting review, grouped by safety level.
 * Safety-level gated approval per DRR-072:
 * - CRITICAL: Dual approval (2 superadmins)
 * - HIGH: Single approval (1 architect)
 * - MEDIUM/LOW: Auto-approved
 *
 * FERP Principles:
 * - Data-driven: Approval items from GraphQL
 * - Safety routing: chaos-ingestion-approval-routing config
 */

import { useMemo } from 'react';
import {
  Stack,
  Card,
  Text,
  Group,
  Badge,
  Button,
  Table,
  Alert,
  ThemeIcon,
} from '@mantine/core';
import {
  IconCheck,
  IconX,
  IconShieldCheck,
  IconAlertTriangle,
  IconClock,
  IconEye,
} from '@tabler/icons-react';
import {
  GqlApprovalStatus,
  useListApprovalsByStatusQuery,
  useUpdateApprovalMetadataMutation,
} from '@/generated/graphql';

// ============================================================================
// Types
// ============================================================================

interface ApprovalItem {
  id: string;
  entityType?: string | null;
  status: string;
  decision?: string | null;
  approvalType: string;
  approvalStrategy: string;
  approverRole: string;
  createdAt: string;
  expiresAt?: string | null;
  decidedAt?: string | null;
  updatedAt: string;
  priority?: string;
}

export interface ApprovalQueueProps {
  tenantId: string;
}

// ============================================================================
// Safety level config
// ============================================================================

const SAFETY_CONFIG: Record<string, { color: string; approvalLabel: string }> = {
  CRITICAL: { color: 'red', approvalLabel: 'Dual Approval Required' },
  HIGH: { color: 'orange', approvalLabel: 'Single Approval Required' },
  MEDIUM: { color: 'yellow', approvalLabel: 'Simple Review' },
  LOW: { color: 'green', approvalLabel: 'Auto-Approved' },
};

// ============================================================================
// Component
// ============================================================================

export function ApprovalQueue({
  tenantId,
}: ApprovalQueueProps): React.ReactElement {
  const { data, loading, error, refetch } = useListApprovalsByStatusQuery({
    variables: { tenantId, status: GqlApprovalStatus.PENDING },
    skip: !tenantId,
    pollInterval: 30000, // Poll every 30s for new approvals
  });

  const [updateApproval, { loading: approving }] = useUpdateApprovalMetadataMutation();

  const approvals: ApprovalItem[] = useMemo(
    () =>
      (data?.listApprovalsByStatus?.items ?? []).map((item) => ({
        id: item.id,
        entityType: item.entityType,
        status: item.status,
        decision: item.decision ?? null,
        approvalType: item.approvalType,
        approvalStrategy: item.approvalStrategy,
        approverRole: item.approverRole,
        createdAt: item.createdAt,
        expiresAt: item.expiresAt ?? null,
        decidedAt: item.decidedAt ?? null,
        updatedAt: item.updatedAt,
      })),
    [data],
  );

  // Group by priority level
  const grouped = useMemo(() => {
    const groups: Record<string, ApprovalItem[]> = {
      CRITICAL: [],
      HIGH: [],
      MEDIUM: [],
      LOW: [],
    };
    for (const item of approvals) {
      const level = item.priority || 'HIGH';
      if (groups[level]) {
        groups[level].push(item);
      } else {
        groups.HIGH?.push(item);
      }
    }
    return groups;
  }, [approvals]);

  const handleApprove = async (approvalId: string) => {
    await updateApproval({
      variables: {
        tenantId,
        approvalId,
        input: { metadata: { decision: 'APPROVED' } },
      },
    });
    await refetch();
  };

  const handleReject = async (approvalId: string) => {
    await updateApproval({
      variables: {
        tenantId,
        approvalId,
        input: { metadata: { decision: 'REJECTED' } },
      },
    });
    await refetch();
  };

  if (loading) {
    return (
      <Card withBorder>
        <Text c="dimmed" ta="center" py="lg">Loading approval queue...</Text>
      </Card>
    );
  }

  if (error) {
    return (
      <Alert color="red" title="Failed to Load Approvals" icon={<IconAlertTriangle size={16} />}>
        {error.message}
      </Alert>
    );
  }

  if (approvals.length === 0) {
    return (
      <Stack gap="md">
        <Alert color="blue" title="No Pending Approvals" icon={<IconCheck size={16} />}>
          All generated configurations have been reviewed.
          Upload documents to generate new configs for review.
        </Alert>

        {/* Approval routing reference */}
        <Card withBorder>
          <Text fw={600} mb="xs">Safety-Level Routing (DRR-072)</Text>
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Safety Level</Table.Th>
                <Table.Th>Approvers</Table.Th>
                <Table.Th>SLA</Table.Th>
                <Table.Th>Review Depth</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              <Table.Tr>
                <Table.Td><Badge color="red" size="sm">CRITICAL</Badge></Table.Td>
                <Table.Td>2 (dual approval)</Table.Td>
                <Table.Td>48 hours</Table.Td>
                <Table.Td>Full</Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Td><Badge color="orange" size="sm">HIGH</Badge></Table.Td>
                <Table.Td>1 (single approval)</Table.Td>
                <Table.Td>72 hours</Table.Td>
                <Table.Td>Standard</Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Td><Badge color="yellow" size="sm">MEDIUM</Badge></Table.Td>
                <Table.Td>0 (simple review)</Table.Td>
                <Table.Td>Auto</Table.Td>
                <Table.Td>Light</Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Td><Badge color="green" size="sm">LOW</Badge></Table.Td>
                <Table.Td>0 (auto-approve)</Table.Td>
                <Table.Td>Auto</Table.Td>
                <Table.Td>None</Table.Td>
              </Table.Tr>
            </Table.Tbody>
          </Table>
        </Card>
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      {/* Summary */}
      <Group gap="md">
        <Badge color="red" size="lg" variant="light">
          {grouped.CRITICAL?.length} CRITICAL
        </Badge>
        <Badge color="orange" size="lg" variant="light">
          {grouped.HIGH?.length} HIGH
        </Badge>
        <Badge color="yellow" size="lg" variant="light">
          {grouped.MEDIUM?.length} MEDIUM
        </Badge>
        <Badge color="green" size="lg" variant="light">
          {grouped.LOW?.length} LOW
        </Badge>
      </Group>

      {/* CRITICAL approvals (always shown first) */}
      {(grouped.CRITICAL?.length ?? 0) > 0 && (
        <Card withBorder style={{ borderColor: 'var(--mantine-color-red-4)' }}>
          <Group mb="md" gap="xs">
            <ThemeIcon color="red" size="sm" variant="light">
              <IconShieldCheck size={14} />
            </ThemeIcon>
            <Text fw={600} c="red">CRITICAL — Dual Approval Required</Text>
          </Group>
          <ApprovalTable
            items={grouped.CRITICAL ?? []}
            onApprove={handleApprove}
            onReject={handleReject}
            approving={approving}
          />
        </Card>
      )}

      {/* HIGH approvals */}
      {(grouped.HIGH?.length ?? 0) > 0 && (
        <Card withBorder>
          <Group mb="md" gap="xs">
            <ThemeIcon color="orange" size="sm" variant="light">
              <IconEye size={14} />
            </ThemeIcon>
            <Text fw={600} c="orange">HIGH — Single Approval Required</Text>
          </Group>
          <ApprovalTable
            items={grouped.HIGH ?? []}
            onApprove={handleApprove}
            onReject={handleReject}
            approving={approving}
          />
        </Card>
      )}

      {/* MEDIUM/LOW (auto-approved, shown for reference) */}
      {((grouped.MEDIUM?.length ?? 0) > 0 || (grouped.LOW?.length ?? 0) > 0) && (
        <Card withBorder>
          <Text fw={600} mb="md" c="dimmed">MEDIUM/LOW — Auto-Approved</Text>
          <ApprovalTable
            items={[...(grouped.MEDIUM ?? []), ...(grouped.LOW ?? [])]}
            onApprove={handleApprove}
            onReject={handleReject}
            approving={approving}
            autoApproved
          />
        </Card>
      )}
    </Stack>
  );
}

/** Reusable approval items table */
function ApprovalTable({
  items,
  onApprove,
  onReject,
  approving,
  autoApproved = false,
}: Readonly<{
  items: ApprovalItem[];
  onApprove: (id: string) => void;
  onReject: (id: string) => void;
  approving: boolean;
  autoApproved?: boolean;
}>): React.ReactElement {
  return (
    <Table>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>Approval</Table.Th>
          <Table.Th>Priority</Table.Th>
          <Table.Th>Status</Table.Th>
          <Table.Th>Expires</Table.Th>
          <Table.Th>Actions</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {items.map((item) => {
          const level = item.priority ?? 'HIGH';
          const safetyCfg = SAFETY_CONFIG[level] ?? SAFETY_CONFIG.HIGH;
          return (
            <Table.Tr key={item.id}>
              <Table.Td>
                <Text size="sm" fw={500}>{item.id}</Text>
                <Text size="xs" c="dimmed">{item.entityType ?? item.approvalType}</Text>
              </Table.Td>
              <Table.Td>
                <Badge size="sm" variant="light" color={safetyCfg?.color}>
                  {level}
                </Badge>
              </Table.Td>
              <Table.Td>
                <Text size="sm">{item.status}</Text>
              </Table.Td>
              <Table.Td>
                <Group gap={4}>
                  <IconClock size={12} />
                  <Text size="xs">{item.expiresAt ? new Date(item.expiresAt).toLocaleDateString() : 'N/A'}</Text>
                </Group>
              </Table.Td>
              <Table.Td>
                {autoApproved ? (
                  <Badge color="green" size="sm" variant="light">Auto-Approved</Badge>
                ) : (
                  <Group gap="xs">
                    <Button
                      size="xs"
                      color="green"
                      variant="light"
                      leftSection={<IconCheck size={12} />}
                      loading={approving}
                      onClick={() => onApprove(item.id)}
                    >
                      Approve
                    </Button>
                    <Button
                      size="xs"
                      color="red"
                      variant="subtle"
                      leftSection={<IconX size={12} />}
                      loading={approving}
                      onClick={() => onReject(item.id)}
                    >
                      Reject
                    </Button>
                  </Group>
                )}
              </Table.Td>
            </Table.Tr>
          );
        })}
      </Table.Tbody>
    </Table>
  );
}

export default ApprovalQueue;
